import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperclip } from "@fortawesome/free-solid-svg-icons";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import MyTable from "../../../components/MyTable";
import MyDropdownSelect from "../../../components/inputs/MyDropdownSelect";
import { useBoardItem } from "../../../hooks/useBoardItem";
import { AuthContext } from "../../../context/AuthProvider";
import { SiteContext } from "../../../context/SiteProvider";
import {
  CheckRoleAndPermissionDisplay,
  checkRoleAndPermissions,
  getPermissionMessage,
} from "../../../components/CheckRoleAndPermission";
import {
  BOARD_PERMISSION,
  NON_MEMBER_PERMISSIONS,
} from "../../../constants/permission";

const Research01 = ({ boardCode }) => {
  const navigate = useNavigate();
  const { useGetBoardItems, searchBoardItemsMutation, useGetBoard } =
    useBoardItem();
  const { roleCode, isProfileLoading, isAdmin } = useContext(AuthContext);
  const { isMobile } = useContext(SiteContext);

  const keySearchOptions = [
    {
      value: "title",
      label: "제목",
    },
    {
      value: "body",
      label: "내용",
    },
    {
      value: "writerName",
      label: "글쓴이",
    },
  ];

  const headers = [
    {
      id: "title",
      title: "제목",
      style: {
        width: 350,
        minWidth: 250,
        textAlign: "center",
      },
    },
    {
      id: "files",
      title: "파일",
      style: {
        width: 60,
        minWidth: 60,
        textAlign: "center",
      },
    },
    {
      id: "author",
      title: "작성자",
      style: {
        width: 120,
        minWidth: 120,
        textAlign: "center",
      },
    },
    {
      id: "createdDate",
      title: "작성일",
      style: {
        width: 100,
        minWidth: 100,
        textAlign: "center",
      },
    },
    {
      id: "readCount",
      title: "조회",
      style: {
        width: 60,
        minWidth: 60,
        textAlign: "center",
      },
    },
  ];

  const nonPermissions = NON_MEMBER_PERMISSIONS[boardCode];

  const isFirstRender = useRef(true);

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [maxPage, setMaxPage] = useState(1);
  const [content, setContent] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [keySearch, setKeySearch] = useState(keySearchOptions[0].value);
  const [total, setTotal] = useState(0);

  const { data: board, isLoading: isBoardLoading } = useGetBoard(
    { boardCode },
    (data) => data.data
  );

  const { data, isLoading } = useGetBoardItems({
    size: board?.pageSize,
    page,
    sort: "createdDate,Desc",
    boardCode: board?.boardCode,
    enabled: checkRoleAndPermissions(
      roleCode,
      board,
      BOARD_PERMISSION.ACCESS,
      nonPermissions,
      isAdmin
    ),
  });

  const paginationProps = useMemo(
    () => ({
      size: board?.pageSize,
      page,
      setPage,
      maxPage,
      total,
    }),
    [board?.pageSize, maxPage, page, total]
  );

  const searchProps = useMemo(
    () => ({
      additionalFilter: (
        <div className="md:w-[130px] w-[70px]">
          {isMobile ? (
            <MyDropdownSelect
              outlined={false}
              defaultValue={keySearch}
              name="keySearch"
              options={keySearchOptions}
              onChange={setKeySearch}
              className="w-full"
              style={{ paddingLeft: 0, paddingRight: 0 }}
            />
          ) : (
            <MyDropdownSelect
              outlined={false}
              defaultValue={keySearch}
              name="keySearch"
              options={keySearchOptions}
              onChange={setKeySearch}
              className="w-full px-2"
            />
          )}
        </div>
      ),
      keyword,
      setKeyword: (val) => {
        setKeyword(val);
        if (val !== keyword) {
          searchData(val);
        }
      },
      onClickAdd: () =>
        checkRoleAndPermissions(
          roleCode,
          board,
          BOARD_PERMISSION.WRITE,
          nonPermissions,
          isAdmin
        )
          ? navigate("/Research/MemberBoard/Add")
          : alert(getPermissionMessage(BOARD_PERMISSION.WRITE)),
    }),
    // eslint-disable-next-line
    [keySearch, keyword, isMobile, roleCode, board, isAdmin]
  );

  const mapData = (listData) => {
    setMaxPage(listData.totalPages);
    setTotal(listData.totalElements);

    const newContent = listData.content?.map((item) => {
      const newTitle = (
        <p
          className="overflow-hidden text-ellipsis line-clamp-1 text-gray-dark text-left md:text-lg sm:text-base text-sm"
          style={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            wordBreak: "break-all",
          }}
        >
          {item.title}
        </p>
      );

      return {
        ...item,
        title: newTitle,
        createdDate: dayjs(item.createdDate).isValid()
          ? dayjs(item.createdDate).format("YYYY-MM-DD")
          : "-",
        author: item?.writer?.name || "-",
        files: item.attachedFiles?.length ? (
          <FontAwesomeIcon icon={faPaperclip} size="md" />
        ) : (
          ""
        ),
        readCount: item.readCount === 0 ? "0" : item.readCount,
      };
    });

    setContent(newContent);
  };

  const searchData = (val) => {
    if (
      checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.ACCESS,
        nonPermissions,
        isAdmin
      )
    ) {
      setLoading(true);
      searchBoardItemsMutation.mutate(
        {
          page,
          size: board?.pageSize,
          boardCode: board?.boardCode,
          sort: "createdDate,Desc",
          keySearch: [keySearch],
          keyword: val,
        },
        {
          onSuccess: (res) => {
            mapData(res);
          },
          onSettled: () => {
            setLoading(false);
          },
        }
      );
    } else {
      alert(getPermissionMessage(BOARD_PERMISSION.ACCESS));
    }
  };

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading && data?.data) {
      mapData(data.data);
    }
  }, [isLoading, data]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (keyword) {
      searchData(keyword);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keySearch]);

  return !isProfileLoading ? (
    <CheckRoleAndPermissionDisplay
      showMessage={!isBoardLoading && !!board}
      board={board}
      permission={BOARD_PERMISSION.ACCESS}
      boardCode={board?.boardCode}
    >
      <MyTable
        showIndex
        showAddBtn={checkRoleAndPermissions(
          roleCode,
          board,
          BOARD_PERMISSION.WRITE,
          nonPermissions,
          isAdmin
        )}
        addText="글 작성하기"
        canClick
        headers={headers}
        data={content}
        dataKey="itemNo"
        loading={loading}
        searchProps={searchProps}
        paginationProps={paginationProps}
        onRowClick={(row) => {
          if (
            checkRoleAndPermissions(
              roleCode,
              board,
              BOARD_PERMISSION.READ,
              nonPermissions,
              isAdmin
            )
          ) {
            navigate(`/Research/MemberBoard/${row.itemNo}`);
          } else {
            alert(getPermissionMessage(BOARD_PERMISSION.READ));
          }
        }}
      />
    </CheckRoleAndPermissionDisplay>
  ) : null;
};

export default Research01;
